import { useEffect, useState, useCallback } from 'react'

/**
 * Returns an `hp`-equivalent helper that recomputes on window resize AND
 * compensates for the dashboard's auto-scale transform.
 *
 * The dashboard (see useAutoScale + DashboardLayout) renders its inner content
 * at a *virtual* height of `100/scale vh` and then visually shrinks it with a
 * CSS transform when the window is below the design baseline. Heights based on
 * the raw `window.innerHeight` therefore occupy an ever-smaller fraction of
 * that larger virtual layout as the window shrinks, which shows up as growing
 * empty space inside fixed-height lists.
 *
 * To keep a constant proportion of the (virtual) sidebar at any window size we
 * compute `value * vh / scale`, mirroring the same scale math used by
 * useAutoScale.
 */

const DESIGN_WIDTH = 1920
const DESIGN_HEIGHT = 1080
const MIN_SCALE = 0.55

const computeScale = (): number => {
  const sw = window.innerWidth / DESIGN_WIDTH
  const sh = window.innerHeight / DESIGN_HEIGHT
  const raw = Math.min(sw, sh)
  return Math.max(MIN_SCALE, Math.min(1, raw))
}

const computeVh = (): number => window.innerHeight / 100

const useResponsiveHp = (): ((value: number) => number) => {
  const [{ vh, scale }, setMetrics] = useState(() => ({
    vh: computeVh(),
    scale: computeScale(),
  }))

  const update = useCallback(() => {
    setMetrics({ vh: computeVh(), scale: computeScale() })
  }, [])

  useEffect(() => {
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [update])

  return useCallback((value: number) => (value * vh) / scale, [vh, scale])
}

export default useResponsiveHp
